/**
 * PWAUpdateToast — subtle update-available prompt.
 *
 * Uses vite-plugin-pwa's virtual:pwa-register/react hook to detect
 * when a new service worker has installed and is waiting to activate.
 *
 * Shows a soft glass-morphism toast in the bottom-right corner.
 * User clicks → clean hard reload with fresh assets.
 *
 * Non-disruptive: no auto-reload, no modal, no popover.
 * Just a small badge that sits quietly until the user is ready.
 */
import React, { useCallback } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { motion, AnimatePresence } from 'framer-motion'
import { RefreshCw } from 'lucide-react'

const PWAUpdateToast: React.FC = () => {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      // Service worker registered successfully — nothing to do
    },
    onRegisterError(error) {
      console.debug('[PWA] SW registration failed:', error.message)
    },
  })

  const handleRefresh = useCallback(() => {
    updateServiceWorker(true).then(() => {
      window.location.reload()
    })
  }, [updateServiceWorker])

  return (
    <AnimatePresence>
      {needRefresh && (
        <motion.button
          initial={{ opacity: 0, y: 24, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 400, damping: 28 }}
          onClick={handleRefresh}
          className={[
            'fixed bottom-24 right-4 z-[100]',
            'flex items-center gap-2 px-3 py-2',
            'bg-white/80 backdrop-blur-xl border border-white/50',
            'rounded-full shadow-[0_2px_12px_rgba(0,0,0,0.08)]',
            'text-[12px] font-medium text-[#275269]',
            'hover:bg-white hover:shadow-[0_4px_20px_rgba(0,0,0,0.12)]',
            'active:scale-95 transition-all duration-150',
            'cursor-pointer select-none',
          ].join(' ')}
          aria-label="Update available — tap to refresh"
        >
          <RefreshCw size={14} className="text-[#6ec2cf]" />
          <span>Update available</span>
        </motion.button>
      )}
    </AnimatePresence>
  )
}

export default PWAUpdateToast
